import type {
  CrossSectionDataSet,
  CrossSectionTubingDataSet,
  TubingChartContext
} from "./d";
import {
  generateCrossSectionDataSetTitle,
  generateEquipmentDataPoints,
  generateMLEDataPoints
} from "./calculateCrossSectionDataSets";
import { getTubingCableODIn } from "@/libs/calculations/cables";
import { convertSmallLengthUnits } from "@/libs/units/length";

/**
 * Generates datasets representing the tubing cross-sections
 *
 * @param context The cross-section chart context
 * @returns An array of datasets representing the tubing configurations (top and bottom)
 */
export const getTubingChartDataSet = (
  tubingChartContext: TubingChartContext[]
): CrossSectionTubingDataSet[] => {
  const tubingChartSet: CrossSectionTubingDataSet[] = [];
  for (const tubingChart of tubingChartContext) {
    const tubingChartDataSet: CrossSectionDataSet[] = [];
    // Radius =$Inputs.F4/2, Center X, Center Y
    const casingInnerRadius =
      (tubingChart.casingDataContext?.innerDiameterIn || 0) / 2;
    const casingInnerCenterX = 0;
    const casingInnerCenterY = 0;
    const casingInnerPoints = generateEquipmentDataPoints(
      casingInnerRadius,
      casingInnerCenterX,
      casingInnerCenterY
    );
    const casingDriftRadius =
      (tubingChart.casingDataContext?.driftDiameterIn || 0) / 2;
    const casingDriftCenterX = 0;
    const casingDriftCenterY = 0;
    const casingDriftPoints = generateEquipmentDataPoints(
      casingDriftRadius,
      casingDriftCenterX,
      casingDriftCenterY
    );
    tubingChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Casing ID",
        tubingChart.casingDataContext?.innerDiameterIn || 0,
        tubingChart.lengthUnit
      ),
      xAxisLabel: `${tubingChart.lengthUnit.label}`,
      yAxisLabel: `${tubingChart.lengthUnit.label}`,
      points: casingInnerPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, tubingChart.lengthUnit),
          y: convertSmallLengthUnits(pt.y, tubingChart.lengthUnit)
        };
      })
    });
    tubingChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Casing Drift ID",
        tubingChart.casingDataContext?.driftDiameterIn || 0,
        tubingChart.lengthUnit
      ),
      xAxisLabel: `${tubingChart.lengthUnit.label}`,
      yAxisLabel: `${tubingChart.lengthUnit.label}`,
      points: casingDriftPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, tubingChart.lengthUnit),
          y: convertSmallLengthUnits(pt.y, tubingChart.lengthUnit)
        };
      })
    });
    // Radius=$Inputs.D15/2, Center X, Center Y
    const tubingDimRadius =
      tubingChart.tubingDataContext.tubingCouplingODIn / 2;
    const tubingDimCenterX = -casingDriftRadius + tubingDimRadius;
    const tubingDimCenterY = 0;
    const tubingDimPoints = generateEquipmentDataPoints(
      tubingDimRadius,
      tubingDimCenterX,
      tubingDimCenterY
    );
    tubingChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Tubing Coupling OD",
        tubingChart.tubingDataContext.tubingCouplingODIn,
        tubingChart.lengthUnit
      ),
      xAxisLabel: `${tubingChart.lengthUnit.label}`,
      yAxisLabel: `${tubingChart.lengthUnit.label}`,
      points: tubingDimPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, tubingChart.lengthUnit),
          y: convertSmallLengthUnits(pt.y, tubingChart.lengthUnit)
        };
      })
    });
    // Cable Upper Dim
    const cableUpperRadius = tubingChart.cableDataContext.depthIn / 2;
    const cableUpperCenterX =
      tubingDimRadius + cableUpperRadius + tubingDimCenterX;
    const cableUpperCenterY =
      tubingChart.cableDataContext.widthIn / 2 -
      tubingChart.cableDataContext.depthIn / 2;
    // Cable Lower Dim
    const cableLowerRadius = cableUpperRadius;
    const cableLowerCenterX = cableUpperCenterX;
    const cableLowerCenterY = -cableUpperCenterY;
    const cableDimPoints = generateMLEDataPoints(
      cableUpperRadius,
      cableUpperCenterX,
      cableUpperCenterY,
      cableLowerRadius,
      cableLowerCenterX,
      cableLowerCenterY
    );
    tubingChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Cable Depth",
        tubingChart.cableDataContext.depthIn,
        tubingChart.lengthUnit
      ),
      xAxisLabel: `${tubingChart.lengthUnit.label}`,
      yAxisLabel: `${tubingChart.lengthUnit.label}`,
      points: cableDimPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, tubingChart.lengthUnit),
          y: convertSmallLengthUnits(pt.y, tubingChart.lengthUnit)
        };
      })
    });

    // Tubing and Cable Dim
    const tubingAndCableOuterDiameterIn = getTubingCableODIn(
      tubingChart.tubingDataContext.tubingCouplingODIn,
      tubingChart.cableDataContext.widthIn,
      tubingChart.cableDataContext.depthIn
    );

    const tubingAndCableRadius = tubingAndCableOuterDiameterIn / 2;
    const tubingAndCableCenterX =
      tubingAndCableRadius - tubingDimRadius + tubingDimCenterX;

    const tubingAndCableCenterY = 0;
    const tubingAndCableDimPoints = generateEquipmentDataPoints(
      tubingAndCableRadius,
      tubingAndCableCenterX,
      tubingAndCableCenterY
    );
    tubingChartDataSet.push({
      title: generateCrossSectionDataSetTitle(
        "Tubing + Cable OD",
        tubingAndCableOuterDiameterIn,
        tubingChart.lengthUnit
      ),
      xAxisLabel: `${tubingChart.lengthUnit.label}`,
      yAxisLabel: `${tubingChart.lengthUnit.label}`,
      points: tubingAndCableDimPoints.map(pt => {
        return {
          x: convertSmallLengthUnits(pt.x, tubingChart.lengthUnit),
          y: convertSmallLengthUnits(pt.y, tubingChart.lengthUnit)
        };
      })
    });
    tubingChartSet.push({
      title: `${tubingChart.tubingDataContext.tubingOrientation} Tubing + Cable ${tubingChart.cableDataContext.cableIndex}`,
      chartSet: tubingChartDataSet
    });
  }
  return tubingChartSet;
};
